
export default {
    props: {
        survey: { type: Object, required: true },
        appointment: { type: Object, default: null }, // {datetime, timezone, method, agent_name, manage_token}
        // The appointment lookup errored (vs. genuinely no appointment) —
        // suppresses the schedule CTA, see the template note.
        lookupFailed: { type: Boolean, default: false },
        // The AEP confirmation (#656 comment 3): /renewal/done passes it;
        // /checkup/done never does, and renders byte-identical to before.
        // The renewal flow ends in an emailed recommendation, so the
        // scheduling CTA and call-prep copy give way to the promise-date
        // callout and the while-you-wait list.
        aep: { type: Boolean, default: false },
        // A selfserve client finishing /checkup/done (pages/checkup/done.vue
        // passes the store getter). They are not getting a consultation, so
        // with no appointment on file the advisor, scheduling and call-prep
        // copy gives way to a saved-answers confirmation and a number to
        // call. With an appointment (an enrolled selfserve client can book an
        // email consultation) the page reads as it does for anyone. `aep`
        // wins if both are ever passed.
        selfserve: { type: Boolean, default: false },
    },
    computed: {
        firstName () { return this.survey.preferred_name || this.survey.first_name },
        // Mirrors WelcomeStep.vue's appointment guard: an appointment is only
        // "real" once it has a datetime (matches state.hasConsult in the demo).
        hasAppointment () { return !!(this.appointment && this.appointment.datetime) },
        // Selfserve with nothing booked: no consultation to frame the page around.
        noConsult () { return this.selfserve && !this.aep && !this.hasAppointment },
        prepHeading () {
            if (this.aep) { return 'While you wait' }
            return this.noConsult ? 'Good to know' : 'Before your call'
        },
        answersNote () {
            return this.noConsult
                ? "Securely saved and seen only by the Doctor's Choice team."
                : 'Securely saved and shared only with your advisor.'
        },
        // Checkup variants are the DEMO's renderDone() copy verbatim; the aep
        // line is Dien's (#656 comment 3), regardless of any appointment. The
        // selfserve line (no appointment) was written for the Plans hide,
        // 2026-10-01.
        lede () {
            if (this.aep) {
                return 'Your advisor will review your information. Your personalized recommendation will arrive by email.'
            }
            if (this.hasAppointment) {
                return 'Your advisor now has everything they need to prepare personalized recommendations for you.'
            }
            if (this.selfserve) {
                return "Everything you shared is saved to your account, so you won't need to repeat it."
            }
            return "Schedule your consultation whenever you're ready. Your advisor will come fully prepared."
        },
        // The stamped promise, rendered "Thursday, October 15" — always a
        // specific date, never "3 business days" (Dien's rule).
        promiseDate () {
            const due = this.survey.aep_recommendation_due
            if (!due) { return null }
            const m = this.$moment(due, 'YYYY-MM-DD')
            return m.isValid() ? m.format('dddd, MMMM D') : null
        },
        // Same formatting as WelcomeStep.vue's apptWhen computed.
        apptWhen () {
            if (!this.hasAppointment) { return '' }
            const m = this.appointment.timezone
                ? this.$moment.tz(this.appointment.datetime, this.appointment.timezone)
                : this.$moment(this.appointment.datetime)
            return m.format('dddd, MMMM D [at] h:mm A z')
        },
        methodLabel () {
            if (!this.hasAppointment) { return '' }
            return { phone: 'Phone call', video: 'Video call', email: 'Email' }[this.appointment.method] || 'Consultation'
        },
        // DEMO's row-advisor reads "Tiffany Caro, Licensed Advisor.
        // She'll call you at (206) 555-0188" — gendering the advisor from a
        // fixed pronoun isn't safe (agent_name alone doesn't tell us gender),
        // so this substitutes the neutral "They'll" here, otherwise matching
        // WelcomeStep.vue's apptDetail wording ("will call you at
        // {cell_phone}" / "will meet you online") verbatim.
        advisorDetail () {
            if (!this.hasAppointment) { return '' }
            if (this.appointment.method === 'video') { return " They'll meet you online" }
            if (this.appointment.method === 'phone' && this.survey.cell_phone) {
                return ` They'll call you at ${this.survey.cell_phone}`
            }
            return ''
        },
    },
}
